// Sign in with Apple and Continue with Google on the sign-in and create-account cards (iOS AuthProviderButtons +
// SignInView.formCard), each only when the server has it on and this build can run it (`useAuthOptions`).
// Both end in `signInWithIdToken` with the raw nonce; AuthProvider then sees SIGNED_IN, resolves the membership and
// the signed-out routes redirect, so the button stays busy until the page goes away.
//
// - Apple: Apple's JS in popup mode. Browsers only allow a popup during a click, so the script and nonce are
//   prepared on mount and `signIn()` is called synchronously from the click.
// - Google: Google Identity Services renders Google's own button (its iframe owns the click); we get the credential
//   in a callback. While Supabase checks it, a veil with a spinner covers the button.
// - e2e (env.authStubs): no third-party scripts; plain buttons mint the fake server's test tokens.

import { clsx } from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { appleStubSignIn, prepareAppleSignIn, type AppleResult } from '@/auth/apple';
import { AuthFlowError, authErrorMessage, isOptionTurnedOff } from '@/auth/errors';
import { signInWithIdToken } from '@/auth/flows';
import { googleStubSignIn, mountGoogleButton } from '@/auth/google';
import { env } from '@/env';
import { Spinner } from './ui';

export type ProviderBusy = 'apple' | 'google' | null;

export interface ProviderButtonsProps {
  /** Wording of Apple's button: "Sign in with Apple" or "Sign up with Apple". */
  mode: 'signIn' | 'signUp';
  apple: boolean;
  google: boolean;
  /** Which button is working (only it shows a spinner; everything is disabled meanwhile). */
  busy: ProviderBusy;
  /** Another action on the page is running. */
  disabled?: boolean;
  onBusyChange: (busy: ProviderBusy) => void;
  /** An error to show next to the buttons (null clears it; a cancelled popup stays silent). */
  onError: (message: string | null) => void;
  /** The server refused because the option was turned off meanwhile: re-read the options. */
  onOptionTurnedOff: () => void;
}

export function ProviderButtons(props: ProviderButtonsProps) {
  const { apple, google } = props;
  if (!apple && !google) return null;
  return (
    <div className="flex flex-col gap-3">
      {apple && <AppleButton {...props} />}
      {google && (env.authStubs ? <GoogleStubButton {...props} /> : <GoogleIdentityButton {...props} />)}
    </div>
  );
}

/** Runs a provider sign-in with the shared busy / error handling. Returns false when it failed. */
async function runProvider(
  which: Exclude<ProviderBusy, null>,
  props: ProviderButtonsProps,
  operation: () => Promise<void>,
): Promise<boolean> {
  props.onBusyChange(which);
  props.onError(null);
  try {
    await operation();
    // Signed in: AuthProvider takes over and this page unmounts. Stay busy so nothing else can be clicked.
    return true;
  } catch (e) {
    props.onError(authErrorMessage(e));
    if (isOptionTurnedOff(e)) props.onOptionTurnedOff();
    props.onBusyChange(null);
    return false;
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Apple
// ---------------------------------------------------------------------------------------------------------------------

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 814 1000" aria-hidden className={className} fill="currentColor">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

function AppleButton(props: ProviderButtonsProps) {
  const { mode, busy, disabled } = props;
  // One prepared sign-in per mount (script + first nonce load in the background). Null in e2e stub mode or when the
  // build has no Services ID (the options hook then hides the button anyway).
  const prepared = useMemo(() => {
    if (env.authStubs || !env.appleServicesId) return null;
    return prepareAppleSignIn({
      servicesId: env.appleServicesId,
      redirectUri: env.appleRedirectUri ?? `${window.location.origin}/auth/apple`,
    });
  }, []);
  const [ready, setReady] = useState(prepared === null);
  useEffect(() => {
    if (!prepared) return undefined;
    let live = true;
    prepared.ready.then(
      () => live && setReady(true),
      () => live && setReady(true), // a failed load is reported on click (and retried by the next prepare)
    );
    return () => {
      live = false;
    };
  }, [prepared]);

  const finish = async (result: AppleResult) => {
    await signInWithIdToken('apple', result.idToken, result.rawNonce, result.name);
  };

  const onClick = () => {
    if (busy) return;
    if (prepared) {
      // Synchronously from the click, so the popup isn't blocked.
      const pending = prepared.signIn();
      void runProvider('apple', props, async () => finish(await pending));
    } else {
      void runProvider('apple', props, async () => finish(await appleStubSignIn()));
    }
  };

  const isBusy = busy === 'apple';
  const label = mode === 'signUp' ? 'Sign up with Apple' : 'Sign in with Apple';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy !== null || !ready}
      aria-busy={isBusy || undefined}
      aria-label={isBusy ? 'Signing in with Apple' : label}
      className={clsx(
        'flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-black px-4 text-[15px] font-semibold text-white transition',
        'hover:bg-black/85 focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-white/90',
      )}
    >
      {isBusy ? <Spinner /> : <AppleLogo className="h-[18px] w-[18px] -translate-y-px" />}
      <span>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Google
// ---------------------------------------------------------------------------------------------------------------------

function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden className={className}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const googleButtonClass = clsx(
  'flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-[15px] font-semibold text-slate-800 transition',
  'hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:outline-none',
  'disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800',
);

/** e2e: a look-alike button that mints the fake server's Google token. */
function GoogleStubButton(props: ProviderButtonsProps) {
  const { busy, disabled } = props;
  const isBusy = busy === 'google';
  const onClick = () => {
    if (busy) return;
    void runProvider('google', props, async () => {
      const { idToken, rawNonce } = await googleStubSignIn();
      await signInWithIdToken('google', idToken, rawNonce);
    });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy !== null}
      aria-busy={isBusy || undefined}
      aria-label={isBusy ? 'Signing in with Google' : 'Continue with Google'}
      className={googleButtonClass}
    >
      {isBusy ? <Spinner /> : <GoogleLogo className="h-[18px] w-[18px]" />}
      <span>{isBusy ? 'Opening Google…' : 'Continue with Google'}</span>
    </button>
  );
}

/** Google's own button (Google Identity Services); its iframe handles the click and the account picker. */
function GoogleIdentityButton(props: ProviderButtonsProps) {
  const { busy, disabled } = props;
  const host = useRef<HTMLDivElement | null>(null);
  // The GIS callback is registered once per mount; it reads the current props through this ref.
  const latest = useRef(props);
  useEffect(() => {
    latest.current = props;
  });
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const el = host.current;
    const clientId = env.googleWebClientId;
    if (!el || !clientId) return undefined;
    return mountGoogleButton(el, {
      clientId,
      onCredential: (idToken, rawNonce) => {
        const p = latest.current;
        if (p.busy !== null || p.disabled) return;
        void runProvider('google', p, () => signInWithIdToken('google', idToken, rawNonce));
      },
      onError: (error) => {
        // The script couldn't load (offline, blocked by an extension) or Google answered without a token.
        if (!(error instanceof AuthFlowError)) setLoadFailed(true);
        latest.current.onError(authErrorMessage(error));
      },
    });
  }, []);

  const isBusy = busy === 'google';
  const blocked = disabled || (busy !== null && !isBusy);
  return (
    <div className="relative">
      {/* Google's button is a light-scheme iframe; under the dark theme the browser would paint an opaque white box
          behind it unless the frame's color-scheme matches, so the host stays light. */}
      <div
        ref={host}
        className={clsx(
          'flex min-h-11 w-full justify-center [color-scheme:light]',
          (blocked || isBusy) && 'pointer-events-none opacity-60',
        )}
        aria-hidden={blocked || isBusy || undefined}
      />
      {loadFailed && !isBusy && (
        <button type="button" className={clsx(googleButtonClass, 'absolute inset-0')} onClick={() => window.location.reload()}>
          <GoogleLogo className="h-[18px] w-[18px]" />
          <span>Google sign-in didn't load. Reload</span>
        </button>
      )}
      {isBusy && (
        <div
          role="status"
          className="absolute inset-0 flex items-center justify-center gap-2 rounded-2xl bg-white/85 text-sm font-semibold text-slate-700 dark:bg-slate-900/85 dark:text-slate-200"
        >
          <Spinner />
          <span>Signing in with Google…</span>
        </div>
      )}
    </div>
  );
}
